import api from './api';
import type { Asset, AssetStatus, PaginatedResponse } from '@/types';

export interface ListAssetsParams {
  status?: AssetStatus;
  sku?: string;
  page?: number;
  limit?: number;
  search?: string;
}

export interface CreateAssetDto {
  serialNumber: string;
  gpsId?: string;
  productSku: string;
  productName: string;
  lotNumber?: string;
  purchaseCost?: number;
  notes?: string;
}

export interface InventorySummary {
  sku: string;
  productName: string;
  ready: number;
  allocated: number;
  inUse: number;
  qcHold: number;
  repair: number;
  total: number;
}

export const assetsApi = {
  list: async (params: ListAssetsParams = {}) => {
    const response = await api.get<PaginatedResponse<Asset>>('/assets', { params });
    return response.data;
  },

  getBySerial: async (serialNumber: string) => {
    const response = await api.get<Asset>(`/assets/serial/${encodeURIComponent(serialNumber)}`);
    return response.data;
  },

  getBySerialOrGps: async (query: string) => {
    const response = await api.get<Asset>(`/assets/search/${encodeURIComponent(query)}`);
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get<Asset>(`/assets/${id}`);
    return response.data;
  },

  create: async (dto: CreateAssetDto) => {
    const response = await api.post<Asset>('/assets', dto);
    return response.data;
  },

  /** Available units for a SKU, optionally narrowed by serial/GPS or a raw GS1 scan. */
  searchAvailable: async (sku: string, search?: string, limit = 50) => {
    const response = await api.get<{ data: Asset[]; total: number }>('/assets', {
      params: { productSku: sku, available: 'true', search: search || undefined, limit },
    });
    return response.data;
  },

  getAvailable: async (sku: string, limit = 100) => {
    // Without an explicit quantity the backend returns a single FIFO pick;
    // the dispatch screen needs a real list for the operator to choose from.
    const response = await api.get<Asset[]>(
      `/assets/available/${encodeURIComponent(sku)}?quantity=${limit}`,
    );
    return response.data;
  },

  getInventorySummary: async () => {
    const response = await api.get<InventorySummary[]>('/assets/inventory-summary');
    return response.data;
  },

  allocate: async (serialNumber: string, rentalId: string) => {
    const response = await api.post<Asset>(`/assets/${serialNumber}/allocate`, { rentalId });
    return response.data;
  },

  dispatch: async (serialNumber: string, rentalId: string) => {
    const response = await api.post<Asset>(`/assets/${serialNumber}/dispatch`, { rentalId });
    return response.data;
  },

  qcPass: async (serialNumber: string, notes?: string) => {
    const response = await api.post<Asset>(`/assets/${serialNumber}/qc-pass`, { notes });
    return response.data;
  },

  qcFail: async (serialNumber: string, notes?: string) => {
    const response = await api.post<Asset>(`/assets/${serialNumber}/qc-fail`, { notes });
    return response.data;
  },

  repairComplete: async (serialNumber: string, notes?: string) => {
    const response = await api.post<Asset>(`/assets/${serialNumber}/repair-complete`, { notes });
    return response.data;
  },

  retire: async (serialNumber: string, reason: string) => {
    const response = await api.post<Asset>(`/assets/${serialNumber}/retire`, { reason });
    return response.data;
  },

  // ── Receive Rental Product workflow ──────────────────────────────────────

  receiveLookup: async (params: {
    serial?: string;
    trackingNumber?: string;
    orderNumber?: string;
  }) => {
    const response = await api.get<ReceiveLookupResult[]>('/assets/receive/lookup', { params });
    return response.data;
  },

  markReceived: async (id: string, body: { notes?: string }) => {
    const response = await api.post<Asset>(`/assets/${id}/receive`, body);
    return response.data;
  },

  reportIssue: async (id: string, body: { notes: string }) => {
    const response = await api.post<Asset>(`/assets/${id}/report-issue`, body);
    return response.data;
  },

  updateGpsId: async (id: string, gpsId: string | null) => {
    const response = await api.patch<Asset>(`/assets/${id}/gps`, { gpsId });
    return response.data;
  },
};

export interface ReceiveLookupResult {
  id: string;
  serialNumber: string;
  productName: string | null;
  productSku: string;
  assetStatus: AssetStatus;
  rental: {
    id: string;
    shopifyOrderNumber: string | null;
    patientName: string | null;
    rentalStatus: string;
    shippingTrackingNumber: string | null;
    returnTrackingNumber: string | null;
    notes: string | null;
  } | null;
}

export default assetsApi;
